import { formatPercent } from "@appretio/ui-kit";
import type {
  AgCartesianAxisOptions,
  AgCartesianChartOptions,
  AgCartesianSeriesOptions
} from "ag-charts-enterprise";
import { useMemo } from "react";
import type { ForecastMetaDataType } from "../../../hooks/useFetchForecastMetaData";
import {
  formatTimeTick,
  getAxisInterval,
  getAxisMax,
  NORMALIZED_AXIS_START
} from "../../../utils/chartDateUtils";
import { getTimePeriodKeys } from "../../../utils/timePeriodUtils";
import type { ForecastTableControlValues } from "../../tableControls/forecastTableControlValues";
import {
  AXIS_COLOURS,
  AXIS_YKEY,
  GRAPH_COLOURS,
  type KpiAxisCategory
} from "../utils/graphConstants";
import {
  applyCumulativeSum,
  classifyMetric,
  getTraceLabel,
  getYearOffset,
  unpivotRow
} from "../utils/graphTransforms";
import { getLineDash } from "../utils/graphUtils";

/** Parameters for building AG Charts options from selected forecast rows. */
interface UseForecastGraphOptionsParams {
  selectedRows: Record<string, unknown>[];
  timePeriod: ForecastTableControlValues["timePeriod"];
  accumulated: boolean;
  metaData: ForecastMetaDataType | undefined;
  /**
   * Weekly x-axis tick labels: calendar (via ``gregMapping``) or the
   * fiscal period key itself (DAS-6481). Month/quarter ticks are
   * unaffected.
   */
  weekLabels: ForecastTableControlValues["weekLabels"];
  /**
   * Give every plotted series its own auto-scaled y-axis (alternating
   * left/right, coloured to match the line) instead of sharing one axis per
   * category. Lets series of wildly different magnitudes — a +1000s line, a
   * −1000s line, a 10-100s line — each read on their own scale. Off by
   * default: the forecast page keeps category-grouped axes; the range-plan
   * modal opts in.
   */
  axisPerSeries?: boolean;
  /**
   * Axis-grouping mode, consulted only when ``axisPerSeries`` is on:
   *   * ``"per-series"`` (default — the legacy ``axisPerSeries`` behaviour):
   *     one auto-scaled y-axis per rendered series.
   *   * ``"per-category"``: bucket the series onto the shared category axes
   *     (Units / Price / Value / Weeks, plus Percentage for a percent
   *     ``fmt_str``) instead of
   *     one-per-series, so every Unit-based KPI reads on one axis, every Price
   *     KPI on a second, every Value KPI on a third. Keeps the line-style
   *     legend that pairs with ``axisPerSeries`` so the grouped lines stay
   *     legible.
   *
   * The forecast page passes neither prop and keeps the plain shared-axis
   * default (``buildAxes`` + dotted legend); the range-plan modal opts into
   * ``axisPerSeries`` + ``axisGrouping="per-category"``.
   */
  axisGrouping?: "per-series" | "per-category";
}

/** A single per-series y-axis (used when ``axisPerSeries`` is on). */
interface PerSeriesAxis {
  /** Axis record key; the series' ``yKeyAxis`` points at this. */
  key: string;
  position: "left" | "right";
  /** Series colour, mirrored on the axis label so the pairing is legible. */
  color: string;
  isPercentage: boolean;
  /** Series dips below zero — drop the axis ``min`` so it isn't clipped. */
  hasNegative: boolean;
}

/** Builds chart series and tracks which axis categories are used. */
function buildSeriesFromRows(
  selectedRows: Record<string, unknown>[],
  timePeriodKeys: string[],
  accumulated: boolean,
  metaData: ForecastMetaDataType,
  // True only for the true per-series axis layout. ``axisPerSeries`` with
  // ``axisGrouping="per-category"`` resolves to ``false`` here so series bind
  // to the shared category axes instead of one-per-series.
  perSeriesAxisLayout: boolean
): {
  series: AgCartesianSeriesOptions[];
  usedAxes: Set<KpiAxisCategory>;
  axesWithNegatives: Set<KpiAxisCategory>;
  perSeriesAxes: PerSeriesAxis[];
} {
  const series: AgCartesianSeriesOptions[] = [];
  const usedAxes = new Set<KpiAxisCategory>();
  // One axis per rendered series, in render order (only populated when
  // ``axisPerSeries`` is on).
  const perSeriesAxes: PerSeriesAxis[] = [];
  // Axes carrying any below-zero value. Roll-forward metrics (e.g.
  // STOCK_UNITS_TOTAL = prior stock + receipts − sales) legitimately go
  // negative; the axis ``min`` is dropped for these so the dip isn't
  // clipped at the zero baseline (see ``buildAxes``).
  const axesWithNegatives = new Set<KpiAxisCategory>();
  const shadeIndices: Record<KpiAxisCategory, number> = {
    PRICE: 0,
    VALUE: 0,
    UNITS: 0,
    PERCENTAGE: 0,
    WEEKS: 0
  };

  for (const row of selectedRows) {
    const metricName = String(row.metric_name ?? "");
    const classification = classifyMetric(metricName, metaData);
    if (!classification) continue;

    const { axisCategory, isInventory } = classification;
    let data = unpivotRow(row, timePeriodKeys, axisCategory);
    if (data.length === 0) continue;
    data = applyCumulativeSum(data, accumulated, classification);

    const shades = GRAPH_COLOURS[axisCategory];
    const color = shades[shadeIndices[axisCategory] % shades.length];
    shadeIndices[axisCategory]++;

    const yKey = AXIS_YKEY[axisCategory];
    const yName = getTraceLabel(row, metaData);
    const yearOffset = getYearOffset(
      String(row.fiscal_year_name ?? ""),
      metaData
    );
    const hasNegative = data.some((point) => (point[yKey] as number) < 0);
    usedAxes.add(axisCategory);
    if (hasNegative) axesWithNegatives.add(axisCategory);

    // In per-series-axis mode each series binds to its own axis (keyed
    // ``y0``, ``y1``, …); otherwise it shares the category axis (``units`` /
    // ``value`` / …). ``yKeyAxis`` names the axis record key the series reads
    // against.
    const yKeyAxis = perSeriesAxisLayout
      ? `y${perSeriesAxes.length}`
      : axisCategory.toLowerCase();
    if (perSeriesAxisLayout) {
      perSeriesAxes.push({
        key: yKeyAxis,
        position: perSeriesAxes.length % 2 === 0 ? "left" : "right",
        color,
        isPercentage: axisCategory === "PERCENTAGE",
        hasNegative
      });
    }

    if (isInventory) {
      series.push({
        type: "bar",
        data,
        xKey: "chartDate",
        yKey,
        yName,
        yKeyAxis,
        fill: color,
        stroke: color,
        stacked: false,
        grouped: true
      });
    } else {
      series.push({
        type: "line",
        data,
        xKey: "chartDate",
        yKey,
        yName,
        yKeyAxis,
        stroke: color,
        strokeWidth: 2,
        lineDash: getLineDash(yearOffset),
        // ``enabled: false``, not ``size: 0``: a zero-sized marker is still an
        // enabled one, so the tooltip drew a marker swatch for a line the plot
        // renders without any. The swatch now matches the series, dashes and all.
        marker: { enabled: false }
      });
    }
  }
  return { series, usedAxes, axesWithNegatives, perSeriesAxes };
}

/** The shared time x-axis (week labels via ``gregMapping`` or fiscal keys). */
function buildXAxis(
  timePeriod: ForecastTableControlValues["timePeriod"],
  metaData: ForecastMetaDataType,
  weekLabels: ForecastTableControlValues["weekLabels"]
): AgCartesianAxisOptions {
  return {
    type: "time",
    position: "bottom",
    min: NORMALIZED_AXIS_START,
    max: getAxisMax(),
    interval: getAxisInterval(timePeriod),
    label: {
      formatter: ({ value }: { value: Date }) =>
        formatTimeTick(value, timePeriod, weekLabels, metaData.gregMapping)
    }
  };
}

/** One numeric y-axis per rendered series, each auto-scaled to its own data. */
function buildPerSeriesAxes(
  perSeriesAxes: PerSeriesAxis[],
  timePeriod: ForecastTableControlValues["timePeriod"],
  metaData: ForecastMetaDataType,
  weekLabels: ForecastTableControlValues["weekLabels"]
): AgCartesianChartOptions["axes"] {
  const axes: AgCartesianChartOptions["axes"] = {
    x: buildXAxis(timePeriod, metaData, weekLabels)
  };
  for (const axis of perSeriesAxes) {
    axes[axis.key] = {
      type: "number",
      position: axis.position,
      label: axis.isPercentage
        ? {
            color: axis.color,
            formatter: ({ value }: { value: number }) => formatPercent(value, 0)
          }
        : { color: axis.color },
      // One series per axis, so auto-scale freely; only pin a zero baseline
      // when the series stays non-negative.
      min: axis.hasNegative ? undefined : 0,
      ...(axis.isPercentage ? { max: 1 } : {})
    };
  }
  return axes;
}

/** Builds the axes configuration from the set of used axis categories. */
function buildAxes(
  usedAxes: Set<KpiAxisCategory>,
  axesWithNegatives: Set<KpiAxisCategory>,
  timePeriod: ForecastTableControlValues["timePeriod"],
  metaData: ForecastMetaDataType,
  weekLabels: ForecastTableControlValues["weekLabels"]
): AgCartesianChartOptions["axes"] {
  const axes: AgCartesianChartOptions["axes"] = {
    x: buildXAxis(timePeriod, metaData, weekLabels)
  };

  // Pin each axis to a zero baseline UNLESS its data dips below zero, in
  // which case leave ``min`` unset so AG Charts auto-scales to include the
  // negative range instead of clipping it at 0.
  if (usedAxes.has("PRICE")) {
    axes.price = {
      type: "number",
      position: "left",
      label: { color: AXIS_COLOURS.PRICE },
      min: axesWithNegatives.has("PRICE") ? undefined : 0
    };
  }
  if (usedAxes.has("VALUE")) {
    axes.value = {
      type: "number",
      position: "left",
      label: { color: AXIS_COLOURS.VALUE },
      min: axesWithNegatives.has("VALUE") ? undefined : 0
    };
  }
  if (usedAxes.has("UNITS")) {
    axes.units = {
      type: "number",
      position: "right",
      label: { color: AXIS_COLOURS.UNITS },
      min: axesWithNegatives.has("UNITS") ? undefined : 0
    };
  }
  if (usedAxes.has("PERCENTAGE")) {
    axes.percentage = {
      type: "number",
      position: "right",
      label: {
        color: AXIS_COLOURS.PERCENTAGE,
        formatter: ({ value }: { value: number }) => formatPercent(value, 0)
      },
      min: axesWithNegatives.has("PERCENTAGE") ? undefined : 0,
      max: 1
    };
  }
  if (usedAxes.has("WEEKS")) {
    axes.weeks = {
      type: "number",
      position: "right",
      label: { color: AXIS_COLOURS.WEEKS },
      min: axesWithNegatives.has("WEEKS") ? undefined : 0
    };
  }

  return axes;
}

export function useForecastGraphOptions({
  selectedRows,
  timePeriod,
  accumulated,
  metaData,
  weekLabels,
  axisPerSeries = false,
  axisGrouping = "per-series"
}: UseForecastGraphOptionsParams): AgCartesianChartOptions {
  return useMemo(() => {
    const timePeriodKeys = getTimePeriodKeys(timePeriod);
    if (!timePeriodKeys || !metaData || selectedRows.length === 0) {
      return { series: [] } as AgCartesianChartOptions;
    }

    // The "axis opt-in" splits into two independent choices:
    //   * does the chart use the line-style legend? (any ``axisPerSeries`` consumer)
    //   * does it lay out one axis per series, or share the category axes?
    // ``axisGrouping="per-category"`` keeps the legend but shares axes.
    // TODO(forecast-foldback): the range-plan modal opts into
    // ``per-category`` axes (category-grouped scales + line-style legend). If
    // that reads better than the forecast page's plain shared-axis default,
    // consider making it the forecast default too — see
    // specs/domain/range_plan/implementation/{changelog,decisions}.md.
    const perSeriesAxisLayout = axisPerSeries && axisGrouping === "per-series";

    const { series, usedAxes, axesWithNegatives, perSeriesAxes } =
      buildSeriesFromRows(
        selectedRows,
        timePeriodKeys,
        accumulated,
        metaData,
        perSeriesAxisLayout
      );

    return {
      series,
      axes: perSeriesAxisLayout
        ? buildPerSeriesAxes(perSeriesAxes, timePeriod, metaData, weekLabels)
        : buildAxes(
            usedAxes,
            axesWithNegatives,
            timePeriod,
            metaData,
            weekLabels
          ),
      // The ``axisPerSeries`` opt-in (per-series OR per-category) pairs with a
      // line-style legend: drop the marker dot (``marker.size: 0``) and draw
      // the series stroke instead, so the swatch reads as the line's own
      // colour rather than a dominant dot. Plain shared-axis forecast keeps
      // the default dotted legend.
      legend: axisPerSeries
        ? {
            position: "top",
            item: {
              showSeriesStroke: true,
              marker: { size: 0 },
              line: { length: 16, strokeWidth: 2 }
            }
          }
        : { position: "top" }
    } satisfies AgCartesianChartOptions;
  }, [
    selectedRows,
    timePeriod,
    accumulated,
    metaData,
    weekLabels,
    axisPerSeries,
    axisGrouping
  ]);
}
